import { useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';
import CTA from '../../home/components/CTA';
import { useSEO, getServiceSchema } from '../../../utils/seo';

export default function AIIntegrationPage() {
  useSEO({
    title: 'AI Integration for Contractors | Automated Customer Service & Lead Qualification',
    description: 'Leverage cutting-edge AI technology to automate customer interactions, qualify leads 24/7, and provide instant responses. Never miss a lead with AI-powered chatbots and automated scheduling for contractors.',
    keywords: 'AI for contractors, contractor chatbot, automated lead qualification, AI customer service for contractors, contractor automation',
    canonical: '/services/ai-integration',
    schema: getServiceSchema(
      'AI Integration for Contractors',
      'Leverage cutting-edge AI technology to automate customer interactions, qualify leads 24/7, and provide instant responses that keep your pipeline full.'
    )
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600 rounded-2xl mb-6">
              <i className="ri-robot-2-line text-4xl text-white"></i>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              AI Integration for Contractors
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Leverage cutting-edge AI technology to automate customer interactions, qualify leads 24/7, and provide instant responses that keep your pipeline full.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={scrollToContact} className="inline-flex items-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer">
                Get Started <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <a href="#features" className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              AI-Powered Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Never miss a lead again with 24/7 automated customer service
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-24-hours-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-black mb-3">24/7 Automated Responses</h3>
              <p className="text-gray-600">
                AI chatbot responds instantly to customer inquiries any time of day or night.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-search-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-black mb-3">Lead Qualification</h3>
              <p className="text-gray-600">
                Automatically qualify leads by asking the right questions and gathering key information.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-calendar-check-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-black mb-3">Appointment Scheduling</h3>
              <p className="text-gray-600">
                Let customers book appointments directly through AI-powered scheduling assistant.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-black mb-3">Customer Support</h3>
              <p className="text-gray-600">
                Answer common questions instantly and escalate complex issues to your team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div>
              <div className="text-5xl md:text-6xl font-heading font-bold text-red-600 mb-4">95%</div>
              <p className="text-xl text-gray-300">Response Rate Improvement</p>
            </div>
            <div>
              <div className="text-5xl md:text-6xl font-heading font-bold text-red-600 mb-4">3x</div>
              <p className="text-xl text-gray-300">More Qualified Leads</p>
            </div>
            <div>
              <div className="text-5xl md:text-6xl font-heading font-bold text-red-600 mb-4">24/7</div>
              <p className="text-xl text-gray-300">Always Available</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/a0f6da76d26bfb53bf0cf6a4573e2a94.jpg"
                alt="AI Integration"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover"
              />
            </div>
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
                Work Smarter, Not Harder
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Let AI handle the repetitive tasks while you focus on delivering exceptional service.
              </p>
              <div className="space-y-6">
                {[
                  { icon: 'ri-time-line', text: 'Save hours every week on customer communication' },
                  { icon: 'ri-money-dollar-circle-line', text: 'Increase revenue by capturing more leads' },
                  { icon: 'ri-team-line', text: 'Free up your team to focus on high-value work' },
                  { icon: 'ri-bar-chart-box-line', text: 'Get insights into customer needs and behavior' },
                  { icon: 'ri-shield-check-line', text: 'Provide consistent, professional responses every time' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-white`}></i>
                    </div>
                    <p className="text-lg text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}
